import Link from 'next/link';

import { Box, Menu, Tabs, Text } from '@zillow/constellation-10';

import { useScreenSize } from '../../../hooks/useScreenSize';
import {
  moreMenuContainerStyles,
  moreMenuLabelContainerStyles,
  moreMenuLabelTextStyles,
  navigationContainerStyles,
  navItemContainerRecipe,
  navItemContentRecipe,
  navItemMarginStyles,
  navItemTextRecipe,
  tabsListStyles,
  tabStyles,
} from './styles';

type Tab = 'overview' | 'listing' | 'applications' | 'leases' | 'payments' | 'maintenance-tasks';

type Props = {
  listingAlias: string;
  activeTab: Tab;
};

const NAV_ITEM_TITLE = {
  overview: 'Overview',
  listing: 'Listing',
  applications: 'Applications',
  leases: 'Leases',
  payments: 'Payments',
  'maintenance-tasks': 'Maintenance',
} as const;

type NavItemProps = {
  title: string;
  isActive: boolean;
  isInMenuPopper?: boolean;
};

const NavItem = ({ title, isActive, isInMenuPopper = false }: NavItemProps) => {
  return (
    <Box data-testid="NavItem" css={navItemContainerRecipe.raw({ isInMenuPopper })}>
      <Box css={navItemContentRecipe.raw({ isActive, isInMenuPopper })} data-testid="NavItem-ContentContainer">
        <Box css={navItemMarginStyles}>
          <Text css={navItemTextRecipe.raw({ isActive })}>{title}</Text>
        </Box>
      </Box>
    </Box>
  );
};

const Navigation = ({ listingAlias, activeTab }: Props) => {
  const { isScreenMobile } = useScreenSize();

  const renderOverflowNavItems = ({ isInMenuPopper = false }: { isInMenuPopper?: boolean }) => {
    const tabs: Array<{ id: Tab; title: string }> = [
      { id: 'payments', title: NAV_ITEM_TITLE.payments },
      { id: 'maintenance-tasks', title: NAV_ITEM_TITLE['maintenance-tasks'] },
    ];

    if (isInMenuPopper) {
      return tabs.map((tab) => (
        <Menu.Item asChild key={tab.id}>
          <Link
            href={{ pathname: `/properties/[listingAlias]/dashboard/${tab.id}`, query: { listingAlias } }}
            style={{ textDecoration: 'none' }}
          >
            <NavItem title={tab.title} isActive={activeTab === tab.id} isInMenuPopper={true} />
          </Link>
        </Menu.Item>
      ));
    } else {
      return tabs.map((tab) => (
        <Tabs.Tab css={tabStyles} value={tab.id} key={tab.id} id={tab.id}>
          <Link
            href={{ pathname: `/properties/[listingAlias]/dashboard/${tab.id}`, query: { listingAlias } }}
            style={{ textDecoration: 'none' }}
          >
            <NavItem title={tab.title} isActive={activeTab === tab.id} />
          </Link>
        </Tabs.Tab>
      ));
    }
  };
  return (
    <Box css={navigationContainerStyles}>
      <Tabs.Root selected={activeTab}>
        <Tabs.List css={tabsListStyles} aria-label="Navigation">
          <Tabs.Tab css={tabStyles} value="overview">
            <Link
              href={{ pathname: '/properties/[listingAlias]/dashboard/overview', query: { listingAlias } }}
              style={{ textDecoration: 'none' }}
            >
              <NavItem title={NAV_ITEM_TITLE.overview} isActive={activeTab === 'overview'} />
            </Link>
          </Tabs.Tab>

          <Tabs.Tab css={tabStyles} value="listing" id="dashboard/listing">
            <Link
              href={{ pathname: '/properties/[listingAlias]/dashboard/listing', query: { listingAlias } }}
              style={{ textDecoration: 'none' }}
            >
              <NavItem title={NAV_ITEM_TITLE.listing} isActive={activeTab === 'listing'} />
            </Link>
          </Tabs.Tab>

          <Tabs.Tab css={tabStyles} value="applications" id="applications">
            <Link
              href={{ pathname: '/properties/[listingAlias]/dashboard/applications', query: { listingAlias } }}
              style={{ textDecoration: 'none' }}
            >
              <NavItem title={NAV_ITEM_TITLE.applications} isActive={activeTab === 'applications'} />
            </Link>
          </Tabs.Tab>

          <Tabs.Tab css={tabStyles} value="leases" id="leases">
            <Link
              href={{ pathname: '/properties/[listingAlias]/dashboard/leases', query: { listingAlias } }}
              style={{ textDecoration: 'none' }}
            >
              <NavItem title={NAV_ITEM_TITLE.leases} isActive={activeTab === 'leases'} />
            </Link>
          </Tabs.Tab>

          {isScreenMobile ? (
            <Box css={moreMenuContainerStyles}>
              <Menu content={renderOverflowNavItems({ isInMenuPopper: true })} />
              <Box css={moreMenuLabelContainerStyles}>
                <Text css={moreMenuLabelTextStyles}>More</Text>
              </Box>
            </Box>
          ) : (
            renderOverflowNavItems({ isInMenuPopper: false })
          )}
        </Tabs.List>
      </Tabs.Root>
    </Box>
  );
};

export default Navigation;
